import React, { useEffect, useRef, useState } from "react";
import api, { formatErr } from "../../lib/api";
import { Plus, Search, Download, Check, Trash2, X, Settings2, Pencil, GripVertical, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useConfirm } from "../ConfirmDialog";
import { toast } from "sonner";
import { renderInlineMd, ExpandableText } from "../RichText";

const COL_TYPES = ["text", "number", "formula", "date", "post_date", "dropdown", "multi", "checkbox", "status", "feedback", "person", "file", "currency", "approval", "meta_ads_kpi", "blended_roas"];

// Meta Ads KPI options for the column type
const META_KPIS = [
  { id: "spend", label: "Spend" },
  { id: "impressions", label: "Impressions" },
  { id: "reach", label: "Reach" },
  { id: "frequency", label: "Frequency" },
  { id: "clicks", label: "Clicks" },
  { id: "ctr", label: "CTR" },
  { id: "cpm", label: "CPM" },
  { id: "cpc", label: "CPC" },
  { id: "conversions", label: "Conversions" },
  { id: "revenue", label: "Revenue" },
  { id: "purchase_roas", label: "Purchase ROAS" },
];

const META_LEVELS = [
  { id: "campaign", label: "Campaign" },
  { id: "adset", label: "Ad set" },
  { id: "ad", label: "Single ad" },
];

// Formula helpers — column refs by name (case-insensitive)
function evaluateFormula(expr, cols, row, depth = 0) {
  if (!expr || typeof expr !== "string") return "";
  const e = expr.trim().replace(/^=/, "");
  if (!e) return "";
  if (depth > 6) return "—"; // guard against circular references
  // Build var map: column-name -> numeric value (resolving =formulas recursively)
  const vars = {};
  for (const c of cols) {
    const raw = (row.data || {})[c.id];
    let num;
    if (typeof raw === "string" && raw.trim().startsWith("=")) {
      const sub = evaluateFormula(raw, cols, row, depth + 1);
      num = typeof sub === "number" ? sub : parseFloat(sub);
    } else if (c.type === "formula" && c.formula) {
      // column-level formula on a sibling column
      const sub = evaluateFormula(c.formula, cols, row, depth + 1);
      num = typeof sub === "number" ? sub : parseFloat(sub);
    } else {
      num = parseFloat(raw);
    }
    vars[(c.name || "").toLowerCase().replace(/\s+/g, "_")] = Number.isFinite(num) ? num : 0;
  }
  // Replace [Column Name] references with their numeric value
  const replaced = e.replace(/\[([^\]]+)\]/g, (_, name) => {
    const key = name.toLowerCase().replace(/\s+/g, "_");
    return String(vars[key] ?? 0);
  });
  // Only allow safe characters
  if (!/^[\d+\-*/().,\s]+$/.test(replaced)) return "—";
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function(`"use strict"; return (${replaced});`);
    const val = fn();
    if (!Number.isFinite(val)) return "—";
    return Math.round(val * 100) / 100;
  } catch { return "—"; }
}
const SWATCHES = [
  { name: "neutral", bg: "rgba(255,255,255,0.08)", fg: "#FAFAFA", border: "rgba(255,255,255,0.18)" },
  { name: "red",     bg: "rgba(239,68,68,0.16)",   fg: "#FCA5A5", border: "rgba(239,68,68,0.32)" },
  { name: "amber",   bg: "rgba(245,158,11,0.16)",  fg: "#FCD34D", border: "rgba(245,158,11,0.32)" },
  { name: "green",   bg: "rgba(16,185,129,0.16)",  fg: "#6EE7B7", border: "rgba(16,185,129,0.32)" },
  { name: "blue",    bg: "rgba(59,130,246,0.16)",  fg: "#93C5FD", border: "rgba(59,130,246,0.32)" },
  { name: "purple",  bg: "rgba(168,85,247,0.16)",  fg: "#D8B4FE", border: "rgba(168,85,247,0.32)" },
  { name: "pink",    bg: "rgba(236,72,153,0.16)",  fg: "#F9A8D4", border: "rgba(236,72,153,0.32)" },
  { name: "gray",    bg: "rgba(113,113,122,0.16)", fg: "#D4D4D8", border: "rgba(113,113,122,0.32)" },
];
const swatchByName = (name) => SWATCHES.find((s) => s.name === name) || SWATCHES[0];

// ── Chip option editor (no comma issue, with color) ──────────────────
function OptionChips({ options = [], onChange }) {
  const [draft, setDraft] = useState("");

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (options.some((o) => o.value === v)) { setDraft(""); return; }
    onChange([...options, { value: v, color: "neutral" }]);
    setDraft("");
  };
  const remove = (val) => onChange(options.filter((o) => o.value !== val));
  const setColor = (val, color) => onChange(options.map((o) => o.value === val ? { ...o, color } : o));

  return (
    <div className="flex flex-wrap gap-1.5 items-center w-full">
      {options.map((opt) => {
        const sw = swatchByName(opt.color);
        return (
          <div key={opt.value} className="group relative flex items-center gap-1 rounded text-[11px] px-1.5 py-0.5"
            style={{ background: sw.bg, color: sw.fg, border: `1px solid ${sw.border}` }}>
            <span>{opt.value}</span>
            <select value={opt.color || "neutral"} onChange={(e) => setColor(opt.value, e.target.value)}
              className="bg-transparent border-0 text-[10px] outline-none cursor-pointer" title="Color">
              {SWATCHES.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
            </select>
            <button type="button" onClick={() => remove(opt.value)} className="opacity-60 hover:opacity-100"><X className="w-3 h-3" /></button>
          </div>
        );
      })}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); }
          else if (e.key === "Backspace" && !draft && options.length) { remove(options[options.length - 1].value); }
        }}
        onBlur={add}
        placeholder="add option ↵"
        className="bg-transparent border-0 outline-none text-[11px] flex-1 min-w-[80px] px-1"
      />
    </div>
  );
}

function ColumnEditor({ block, onChange, onClose }) {
  // Migrate options to {value, color} objects if they are plain strings (legacy)
  const migrate = (cols) => cols.map((c) => ({
    ...c,
    options: (c.options || []).map((o) => typeof o === "string" ? { value: o, color: "neutral" } : o),
  }));
  const [cols, setCols] = useState(migrate(block.config?.columns || []));
  const [mode, setMode] = useState(block.config?.approval_mode || "whole");

  const update = (idx, patch) => setCols(cols.map((c, i) => i === idx ? { ...c, ...patch } : c));
  const remove = (idx) => setCols(cols.filter((_, i) => i !== idx));
  const add = () => setCols([...cols, { id: crypto.randomUUID(), name: "New column", type: "text", client_writable: false, options: [] }]);

  const save = async () => {
    await onChange({ ...block.config, columns: cols, approval_mode: mode });
    onClose();
  };

  return (
    <div className="tt-card p-4 space-y-2.5 mt-2 anim-fade-up" data-testid="sheet-column-editor">
      <div className="flex items-center justify-between">
        <div className="t-eyebrow">Columns &amp; approval</div>
        <select value={mode} onChange={(e) => setMode(e.target.value)} className="bg-[#0a0a0a] border border-zinc-800 rounded text-[11px] px-2 h-7" data-testid="approval-mode">
          <option value="whole">Whole-sheet approve</option>
          <option value="row">Per-row approve</option>
          <option value="both">Both</option>
        </select>
      </div>
      <ul className="space-y-2">
        {cols.map((c, i) => (
          <li key={c.id} className="bg-[#0a0a0a] border border-zinc-800 rounded-md p-2.5 space-y-1.5">
            <div className="flex flex-wrap gap-1.5 items-center">
              <Input value={c.name} onChange={(e) => update(i, { name: e.target.value })} className="bg-transparent border-zinc-800 flex-1 min-w-[140px] h-7 text-[12px]" />
              <select value={c.type} onChange={(e) => update(i, { type: e.target.value })}
                disabled={!!c.locked}
                className="bg-[#111] border border-zinc-800 rounded text-[11px] px-2 h-7 disabled:opacity-50">
                {COL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <label className="flex items-center gap-1 text-[11px] text-zinc-500">
                <input type="checkbox" checked={!!c.client_writable} onChange={(e) => update(i, { client_writable: e.target.checked })} />
                client edits
              </label>
              {c.locked ? (
                <span className="text-[10px] text-zinc-600 font-mono uppercase tracking-wider px-2">🔒 locked</span>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => remove(i)} className="h-7 w-7 p-0"><Trash2 className="w-3 h-3 text-[#EF4444]" /></Button>
              )}
            </div>
            {/* Placeholder text — shown in empty text / feedback cells */}
            {(c.type === "text" || c.type === "feedback" || !c.type) && (
              <div className="flex items-center gap-2">
                <span className="t-eyebrow text-[9px] shrink-0">Placeholder</span>
                <Input value={c.placeholder || ""}
                  onChange={(e) => update(i, { placeholder: e.target.value })}
                  placeholder={c.type === "feedback" ? "e.g. Leave feedback…" : "e.g. Post copy goes here…"}
                  className="bg-transparent border-zinc-800 h-7 text-[12px] flex-1"
                  data-testid={`col-placeholder-${c.id}`} />
              </div>
            )}
            {(c.type === "dropdown" || c.type === "multi" || c.type === "status") && (
              <div className="border-t border-zinc-900 pt-2">
                <div className="t-eyebrow mb-1.5 text-[9px]">Options</div>
                <OptionChips options={c.options || []} onChange={(opts) => update(i, { options: opts })} />
              </div>
            )}
            {c.type === "formula" && (
              <div className="border-t border-zinc-900 pt-2">
                <div className="t-eyebrow mb-1.5 text-[9px]">Formula · supports [Column Name] refs + +-*/(). Example: <code>=[Spend] / [Revenue]</code></div>
                <Input value={c.formula || ""} onChange={(e) => update(i, { formula: e.target.value })}
                  placeholder="=[Spend] * 1.2 + [Tickets]"
                  className="bg-transparent border-zinc-800 h-7 text-[12px] font-mono" />
              </div>
            )}
            {c.type === "meta_ads_kpi" && (
              <div className="border-t border-zinc-900 pt-2 space-y-2">
                <div className="t-eyebrow text-[9px]">Meta Ads KPI · pulls the latest value for a campaign / ad set / single ad</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <select value={c.meta_level || "campaign"} onChange={(e) => update(i, { meta_level: e.target.value })}
                    className="bg-[#111] border border-zinc-800 rounded text-[11px] px-2 h-7">
                    {META_LEVELS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                  </select>
                  <select value={c.meta_kpi || "spend"} onChange={(e) => update(i, { meta_kpi: e.target.value })}
                    className="bg-[#111] border border-zinc-800 rounded text-[11px] px-2 h-7">
                    {META_KPIS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                  </select>
                </div>
                <Input value={c.meta_target_id || ""} onChange={(e) => update(i, { meta_target_id: e.target.value })}
                  placeholder="Campaign / ad-set / ad ID (leave empty to set per-row)"
                  className="bg-transparent border-zinc-800 h-7 text-[12px] font-mono" />
                <select value={c.meta_date_preset || "last_30d"} onChange={(e) => update(i, { meta_date_preset: e.target.value })}
                  className="bg-[#111] border border-zinc-800 rounded text-[11px] px-2 h-7">
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="last_7d">Last 7 days</option>
                  <option value="last_30d">Last 30 days</option>
                  <option value="last_90d">Last 90 days</option>
                  <option value="maximum">Maximum</option>
                </select>
              </div>
            )}
            {c.type === "blended_roas" && (
              <div className="border-t border-zinc-900 pt-2 space-y-2">
                <div className="t-eyebrow text-[9px]">Blended ROAS · revenue ÷ spend across selected campaigns. Provide comma-separated campaign IDs.</div>
                <Input value={(c.blended_campaign_ids || []).join(",")}
                  onChange={(e) => update(i, { blended_campaign_ids: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  placeholder="123456789, 987654321"
                  className="bg-transparent border-zinc-800 h-7 text-[12px] font-mono" />
                <Input value={c.revenue_column || ""}
                  onChange={(e) => update(i, { revenue_column: e.target.value })}
                  placeholder="(optional) Revenue column name to use instead of Meta revenue"
                  className="bg-transparent border-zinc-800 h-7 text-[12px]" />
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Button size="sm" onClick={add} variant="ghost" className="text-zinc-300 text-[12px] h-8"><Plus className="w-3 h-3 mr-1" />Add column</Button>
        <div className="flex-1" />
        <Button size="sm" variant="ghost" onClick={onClose} className="text-zinc-500 text-[12px] h-8">Cancel</Button>
        <Button size="sm" onClick={save} className="bg-white hover:bg-zinc-200 text-black text-[12px] h-8" data-testid="save-columns">Save</Button>
      </div>
    </div>
  );
}

function computeOnTime(cols, row) {
  const statusCol = cols.find((c) => (c.name || "").toLowerCase() === "status");
  if (!statusCol) return null;
  if ((row.data || {})[statusCol.id] !== "Delivered") return null;
  const ad = cols.find((c) => (c.name || "").toLowerCase() === "assigned date");
  const tat = cols.find((c) => (c.name || "").toLowerCase().includes("tat"));
  const dd = cols.find((c) => (c.name || "").toLowerCase() === "delivery date");
  if (!ad || !tat || !dd) return null;
  try {
    const adv = new Date(row.data[ad.id]);
    const ddv = new Date(row.data[dd.id]);
    const tatv = parseInt(row.data[tat.id]);
    if (isNaN(tatv)) return null;
    const deadline = new Date(adv.getTime() + tatv * 86400000);
    return ddv <= deadline ? "on time" : "late";
  } catch { return null; }
}

function CellEditor({ col, value, onCommit, readonly, row, onApproveRow, onUnapproveRow, disabled, allCols, isTeam }) {
  const [v, setV] = useState(value ?? "");
  const [editing, setEditing] = useState(false);
  const taRef = useRef(null);
  useEffect(() => { setV(value ?? ""); }, [value]);

  // Auto-grow any textarea inside the sheet cell
  const autosize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = (el.scrollHeight) + "px";
  };
  useEffect(() => { autosize(taRef.current); }, [v]);

  const optsObj = (col.options || []).map((o) => typeof o === "string" ? { value: o, color: "neutral" } : o);

  // Formula column type: render as computed value, no edit
  if (col.type === "formula") {
    const computed = evaluateFormula(col.formula, allCols || [], row || {});
    return (
      <div className="px-3.5 py-2 font-mono text-zinc-200 text-right" title={col.formula || ""} data-testid="sheet-cell-formula">
        {computed === "" || computed === "—" ? <span className="text-zinc-700">—</span> : computed.toLocaleString()}
      </div>
    );
  }

  // Meta Ads / Blended ROAS columns: read-only display, value comes from row.data (we let team paste it or future cron will populate)
  if (col.type === "meta_ads_kpi" || col.type === "blended_roas") {
    return (
      <div className="px-3.5 py-2 text-right" data-testid="sheet-cell-meta">
        {renderDisplay(col, value, row, allCols)}
        <div className="text-[9px] text-zinc-700 font-mono mt-0.5">
          {col.type === "blended_roas" ? `blended · ${(col.blended_campaign_ids || []).length} camp` : `${col.meta_kpi || ""} · ${col.meta_level || "campaign"}`}
        </div>
      </div>
    );
  }

  // Approval column type
  if (col.type === "approval") {
    if (row?.approved) {
      return (
        <div className="px-3.5 py-2 flex items-center gap-2">
          <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider score-good flex items-center gap-1">
            <Check className="w-3 h-3" /> Approved
          </span>
          <div className="flex flex-col leading-tight">
            <span className="text-[10px] text-zinc-500 font-mono truncate">{row.approved_by_name || ""}</span>
            {row.approved_at && (
              <span className="text-[9px] text-zinc-600 font-mono">{new Date(row.approved_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}</span>
            )}
          </div>
          {!disabled && !readonly && onUnapproveRow && (
            <button onClick={() => onUnapproveRow(row.id)} title="Edit approval"
              className="ml-auto text-zinc-600 hover:text-white p-1 rounded"
              data-testid={`unapprove-${row.id}`}>
              <Pencil className="w-3 h-3" />
            </button>
          )}
        </div>
      );
    }
    if (row?.edited_after_approval) {
      return (
        <div className="px-3.5 py-2">
          <button onClick={() => onApproveRow && onApproveRow(row.id)} disabled={readonly || disabled}
            className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider score-okay hover:bg-white/15 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            Re-approve
          </button>
        </div>
      );
    }
    return (
      <div className="px-3.5 py-2">
        <button onClick={() => onApproveRow && onApproveRow(row.id)} disabled={readonly || disabled}
          className={`px-2.5 py-1 rounded text-[11px] uppercase tracking-wider font-medium border transition-all ${
            (readonly || disabled)
              ? "border-zinc-800 text-zinc-700 cursor-not-allowed"
              : "border-zinc-700 text-zinc-300 hover:bg-white hover:text-black hover:border-white"
          }`}>
          ✓ Approve
        </button>
      </div>
    );
  }

  if (readonly) {
    const onTime = allCols ? computeOnTime(allCols, row) : null;
    const showOnTime = onTime && isTeam && (col.name || "").toLowerCase() === "status";
    // For text-like cells, route the read-only display through ExpandableText so
    // long content collapses to ~3 lines with a "View more" affordance.
    const isTextish = !col.type || col.type === "text" || col.type === "feedback";
    return (
      <div className="sheet-cell-display flex flex-col gap-1 px-3.5 py-2">
        {isTextish ? (
          <ExpandableText
            value={value}
            canEdit={false}
            placeholder="—"
            className="text-[13px] leading-relaxed"
            testId="sheet-cell-text"
            title={col.name || "Cell"}
          />
        ) : (
          <div>{renderDisplay(col, value, row, allCols)}</div>
        )}
        {showOnTime && (
          <span className={`text-[9px] uppercase tracking-[0.15em] font-mono ${onTime === "on time" ? "text-emerald-400" : "text-[#EF4444]"}`}>
            {onTime}
          </span>
        )}
      </div>
    );
  }

  const commit = () => { if (v !== (value ?? "")) onCommit(v); };

  if (col.type === "dropdown" || col.type === "status") {
    const cur = optsObj.find((o) => o.value === v);
    const sw = cur ? swatchByName(cur.color) : null;
    const onTime = allCols ? computeOnTime(allCols, row) : null;
    const showOnTime = onTime && isTeam && (col.name || "").toLowerCase() === "status";
    return (
      <div className="relative px-3.5 py-2 flex flex-col gap-1">
        <div className="flex items-center gap-1.5">
          {cur ? (
            <span className="px-1.5 py-0.5 rounded text-[11px] whitespace-nowrap"
              style={{ background: sw.bg, color: sw.fg, border: `1px solid ${sw.border}` }}>{cur.value}</span>
          ) : (
            <span className="text-zinc-600 text-[12px]">—</span>
          )}
          <span className="relative inline-flex items-center justify-center w-5 h-5 text-zinc-400 hover:text-white cursor-pointer rounded hover:bg-zinc-800">
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <select value={v || ""} onChange={(e) => { setV(e.target.value); onCommit(e.target.value); }}
              disabled={disabled}
              className="sheet-native-select absolute inset-0 opacity-0 cursor-pointer w-full h-full">
              <option value="">—</option>
              {optsObj.map((o) => <option key={o.value} value={o.value}>{o.value}</option>)}
            </select>
          </span>
        </div>
        {showOnTime && (
          <span className={`text-[9px] uppercase tracking-[0.15em] font-mono ${onTime === "on time" ? "text-emerald-400" : "text-[#EF4444]"}`}>
            {onTime}
          </span>
        )}
      </div>
    );
  }
  if (col.type === "feedback") {
    const fwritable = !readonly && !disabled;
    // Same UX as the default text cell: not-editing shows ExpandableText,
    // click to edit (if writable) swaps to an auto-growing textarea.
    if (!editing) {
      return (
        <div className={`px-3.5 py-2 ${fwritable ? "cursor-text" : ""}`}
          onClick={fwritable ? () => setEditing(true) : undefined}>
          <ExpandableText
            value={v}
            canEdit={fwritable}
            onCommit={(next) => { setV(next); onCommit(next); }}
            placeholder={col.placeholder || "Leave feedback…"}
            className="text-[13px] leading-relaxed"
            testId="sheet-cell-feedback"
            title={col.name || "Feedback"}
          />
          {row?.last_edited_by_name && (v || row.data?.[col.id]) && (
            <div className="text-[10px] text-zinc-600 font-mono uppercase tracking-wider mt-1">
              {row.last_edited_by_name} · {new Date(row.last_edited_at).toLocaleDateString()}
            </div>
          )}
        </div>
      );
    }
    return (
      <div className="px-3.5 py-2">
        <textarea ref={taRef} value={v || ""} rows={1}
          autoFocus
          onChange={(e) => { setV(e.target.value); autosize(e.target); }}
          onBlur={() => { commit(); setEditing(false); }}
          placeholder={col.placeholder || "Leave feedback…"} disabled={disabled}
          onKeyDown={(e) => { if (e.key === "Escape") { setEditing(false); setV(value ?? ""); } }}
          className="w-full bg-transparent text-zinc-200 outline-none text-[13px] resize-none leading-relaxed focus:ring-2 focus:ring-white/30 rounded-sm overflow-hidden" />
      </div>
    );
  }
  if (col.type === "checkbox") {
    return <div className="px-3.5 py-2.5 flex items-center"><input type="checkbox" checked={!!v} disabled={disabled} onChange={(e) => { setV(e.target.checked); onCommit(e.target.checked); }} className="accent-white" /></div>;
  }
  if (col.type === "person") {
    return <PersonCell value={v} onCommit={(val) => { setV(val); onCommit(val); }} disabled={disabled} teamMembers={col._teamMembers || []} />;
  }
  if (col.type === "date" || col.type === "post_date") {
    return <input type="date" value={v || ""} onChange={(e) => setV(e.target.value)} onBlur={commit} disabled={disabled}
      className="w-full bg-transparent text-zinc-200 px-3.5 py-2.5 outline-none text-[13px] sheet-date-input" />;
  }
  if (col.type === "number" || col.type === "currency") {
    // Per-cell formula: if the value starts with "=", treat as text so the user can edit the expression.
    // Display lives-computes via renderDisplay; here we just allow editing.
    const isFormula = typeof v === "string" && v.trim().startsWith("=");
    return <input type={isFormula ? "text" : "number"} value={v ?? ""} onChange={(e) => setV(e.target.value)} onBlur={commit} disabled={disabled}
      placeholder={isFormula ? undefined : "0"}
      onKeyDown={(e) => {
        // Pressing "=" in a number input is awkward; if user types "=" first, swap to text mode on next keystroke
        if (e.key === "=" && !isFormula) { e.preventDefault(); setV("="); }
        if (e.key === "Enter") e.target.blur();
      }}
      className="w-full bg-transparent text-zinc-200 px-3.5 py-2.5 outline-none text-[13px] font-mono" />;
  }
  // Default text cell:
  //   - Read-only OR not-currently-editing: render display (ExpandableText for plain text,
  //     renderDisplay() for per-cell formulas). Writable cells click to edit.
  //   - Writable + editing: auto-growing textarea (same as before).
  const writable = !readonly && !disabled;
  if (!editing) {
    const isFormulaText = typeof v === "string" && v.trim().startsWith("=");
    return (
      <div className={`px-3.5 py-2 ${writable ? "cursor-text" : ""}`}
        onClick={writable ? () => setEditing(true) : undefined}>
        {isFormulaText ? (
          renderDisplay(col, v, row, allCols)
        ) : (
          <ExpandableText
            value={v}
            canEdit={writable}
            onCommit={(next) => { setV(next); onCommit(next); }}
            placeholder={col.placeholder || "—"}
            className="text-[13px] leading-relaxed"
            testId="sheet-cell-text"
            title={col.name || "Cell"}
          />
        )}
      </div>
    );
  }
  return <textarea ref={taRef} value={v || ""} onChange={(e) => { setV(e.target.value); autosize(e.target); }}
    onBlur={() => { commit(); setEditing(false); }} rows={1}
    autoFocus
    placeholder={col.placeholder || ""}
    disabled={disabled}
    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.target.blur(); } if (e.key === "Escape") { setEditing(false); setV(value ?? ""); } }}
    className="w-full bg-transparent text-zinc-200 outline-none text-[13px] resize-none overflow-hidden px-3.5 py-2" />;
}

function PersonCell({ value, onCommit, disabled, teamMembers }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const arr = Array.isArray(value) ? value : value ? [value] : [];
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const toggle = (uid) => {
    const next = arr.includes(uid) ? arr.filter((x) => x !== uid) : [...arr, uid];
    onCommit(next);
  };
  const tagged = arr.map((uid) => teamMembers.find((m) => m.id === uid)).filter(Boolean);
  return (
    <div className="relative px-3.5 py-2" ref={ref}>
      <button onClick={() => !disabled && setOpen((v) => !v)} disabled={disabled}
        className="flex items-center gap-1 flex-wrap min-h-[24px] w-full text-left">
        {tagged.length === 0 ? (
          <span className="text-zinc-600 text-[12px]">+ assign</span>
        ) : tagged.map((m) => (
          <span key={m.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-zinc-800 border border-zinc-700 text-[10px] text-zinc-200">
            <span className="w-3.5 h-3.5 rounded-full bg-zinc-700 flex items-center justify-center font-display font-semibold text-[8px] uppercase">{m.name?.[0]}</span>
            {m.name}
          </span>
        ))}
      </button>
      {open && (
        <div className="absolute left-3 top-full mt-1 z-30 w-56 bg-[#111] border border-zinc-800 rounded-md shadow-2xl overflow-hidden max-h-64 overflow-y-auto">
          {teamMembers.length === 0 ? (
            <div className="px-3 py-2 text-[11px] text-zinc-600">No team members assigned. Set team in Client Settings.</div>
          ) : teamMembers.map((m) => {
            const on = arr.includes(m.id);
            return (
              <button key={m.id} onClick={() => toggle(m.id)}
                className={`w-full text-left px-3 py-1.5 text-[12px] flex items-center gap-2 hover:bg-zinc-900 ${on ? "text-white" : "text-zinc-400"}`}>
                <span className="w-4 h-4 rounded border border-zinc-700 flex items-center justify-center bg-zinc-900">{on && <Check className="w-3 h-3 text-white" />}</span>
                {m.name}
                <span className="text-[9px] uppercase tracking-wider text-zinc-600 font-mono ml-auto">{m.role}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function renderDisplay(col, value, row, allCols) {
  // Per-cell formula: any text/number/currency value starting with "=" is treated as a live formula
  if (typeof value === "string" && value.trim().startsWith("=") &&
      (col.type === "text" || col.type === "number" || col.type === "currency" || !col.type)) {
    const computed = evaluateFormula(value, allCols || [], row || {});
    const display = computed === "" || computed === "—"
      ? "—"
      : (col.type === "currency" ? `₹${Number(computed).toLocaleString()}` : computed.toLocaleString());
    return (
      <span className="font-mono text-zinc-200 inline-flex items-center gap-1" title={`formula · ${value}`} data-testid="sheet-cell-perCell-formula">
        {display}
        <span className="text-[8px] font-mono text-zinc-600 border border-zinc-800 rounded px-1 py-px">fx</span>
      </span>
    );
  }
  // Formula: evaluate using sibling column refs
  if (col.type === "formula") {
    if (!col.formula) return <span className="text-zinc-700">—</span>;
    const v = evaluateFormula(col.formula, allCols || [], row || {});
    return <span className="font-mono text-zinc-200">{v === "" || v === "—" ? "—" : v.toLocaleString()}</span>;
  }
  // Meta Ads KPI / Blended ROAS — display the cached value stored on the row
  if (col.type === "meta_ads_kpi" || col.type === "blended_roas") {
    const numeric = parseFloat(value);
    if (!Number.isFinite(numeric)) return <span className="text-zinc-700">—</span>;
    const isMoney = col.type === "blended_roas"
      ? false
      : ["spend", "cpm", "cpc", "revenue"].includes(col.meta_kpi || "");
    const isPct = (col.meta_kpi || "") === "ctr";
    const fmt = isMoney ? `₹${numeric.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : (isPct ? `${numeric.toFixed(2)}%` : numeric.toLocaleString(undefined, { maximumFractionDigits: 2 }));
    return <span className="font-mono text-zinc-200" title={col.type === "blended_roas" ? "Blended ROAS" : `${col.meta_kpi} (${col.meta_level || "campaign"})`}>{fmt}</span>;
  }
  if (col.type === "feedback") {
    if (!value) return <div className="px-3.5 py-2 text-zinc-700 text-[13px]">—</div>;
    return (
      <div className="px-3.5 py-2">
        <div className="text-zinc-300 text-[13px] leading-relaxed whitespace-pre-wrap">{value}</div>
        {row?.last_edited_by_name && (
          <div className="text-[10px] text-zinc-600 font-mono uppercase tracking-wider mt-1">
            {row.last_edited_by_name} · {new Date(row.last_edited_at).toLocaleDateString()}
          </div>
        )}
      </div>
    );
  }
  if (value == null || value === "") return <span className="text-zinc-700">—</span>;
  if (col.type === "checkbox") return value ? "✓" : "";
  if (col.type === "person") {
    const arr = Array.isArray(value) ? value : value ? [value] : [];
    if (arr.length === 0) return <span className="text-zinc-700">—</span>;
    const tm = col._teamMembers || [];
    return (
      <div className="flex items-center gap-1 flex-wrap">
        {arr.map((uid) => {
          const m = tm.find((x) => x.id === uid);
          return <span key={uid} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-zinc-800 border border-zinc-700 text-[10px] text-zinc-200">
            <span className="w-3.5 h-3.5 rounded-full bg-zinc-700 flex items-center justify-center font-display font-semibold text-[8px] uppercase">{m?.name?.[0] || "?"}</span>
            {m?.name || "Unknown"}
          </span>;
        })}
      </div>
    );
  }
  if (col.type === "currency") return `₹${Number(value).toLocaleString()}`;
  if (col.type === "status" || col.type === "dropdown") {
    const opts = (col.options || []).map((o) => typeof o === "string" ? { value: o, color: "neutral" } : o);
    const cur = opts.find((o) => o.value === value);
    if (cur) {
      const sw = swatchByName(cur.color);
      return <span className="px-1.5 py-0.5 rounded text-[11px]" style={{ background: sw.bg, color: sw.fg, border: `1px solid ${sw.border}` }}>{value}</span>;
    }
  }
  return String(value);
}

export default function SheetBlock({ block, onUpdateBlock, isTeam }) {
  const [rows, setRows] = useState([]);
  const [editor, setEditor] = useState(false);
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [dragColId, setDragColId] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const { confirm } = useConfirm();
  const rawColumns = block.config?.columns || [];
  // Inject team members into person columns for rendering
  const columns = rawColumns.map((c) => c.type === "person" ? { ...c, _teamMembers: teamMembers } : c);
  const approvalMode = block.config?.approval_mode || "whole";

  const load = async () => {
    const r = await api.get(`/blocks/${block.id}/rows`);
    setRows(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [block.id]);

  // Realtime: when another tab/user mutates a row in THIS block, refresh.
  useEffect(() => {
    const onEvt = (e) => {
      const d = e.detail;
      if (!d || d.block_id !== block.id) return;
      if (d.type === "row_created") {
        setRows((prev) => prev.some((r) => r.id === d.row.id) ? prev : [...prev, d.row]);
      } else if (d.type === "row_updated") {
        setRows((prev) => prev.map((r) => r.id === d.row_id ? { ...r, ...d.patch } : r));
      } else if (d.type === "row_deleted") {
        setRows((prev) => prev.filter((r) => r.id !== d.row_id));
      }
    };
    window.addEventListener("tt-realtime-row", onEvt);
    return () => window.removeEventListener("tt-realtime-row", onEvt);
  }, [block.id]);

  // Fetch team members for person columns (client's assigned teams → their members)
  useEffect(() => {
    const hasPerson = rawColumns.some((c) => c.type === "person");
    if (!hasPerson || !block.client_id) return;
    (async () => {
      try {
        const c = await api.get(`/clients/${block.client_id}`);
        const teamIds = c.data.assigned_team_ids || [];
        if (teamIds.length === 0) { setTeamMembers([]); return; }
        const teams = await Promise.all(teamIds.map((tid) => api.get(`/teams/${tid}`).catch(() => null)));
        const memberIds = new Set();
        teams.filter(Boolean).forEach((t) => (t.data.member_user_ids || []).forEach((m) => memberIds.add(m)));
        if (memberIds.size === 0) { setTeamMembers([]); return; }
        const users = await api.get("/team/users");
        setTeamMembers((users.data || []).filter((u) => memberIds.has(u.id)));
      } catch {}
    })();
  }, [block.client_id, block.config]);

  const hasMetaCols = columns.some((c) => c.type === "meta_ads_kpi" || c.type === "blended_roas");
  const refreshMetaKpis = async () => {
    try {
      const r = await api.post(`/blocks/${block.id}/refresh_meta_kpis`);
      const d = r.data || {};
      if (d.errors && d.errors.length) toast.error(`Refreshed ${d.refreshed || 0}, errors: ${d.errors.join(" · ")}`);
      else toast.success(`Refreshed ${d.refreshed || 0} row(s) · ${d.columns || 0} meta column(s)`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const updateConfig = async (config) => { await onUpdateBlock(block.id, { config }); };
  const addRow = async () => { await api.post(`/blocks/${block.id}/rows`, { data: {} }); load(); };
  const updateCell = async (rowId, colId, value) => {
    try { await api.put(`/rows/${rowId}`, { data: { [colId]: value } }); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const approveRow = async (rowId) => { await api.post(`/rows/${rowId}/approve`); load(); };
  const unapproveRow = async (rowId) => {
    try { await api.post(`/rows/${rowId}/unapprove`); load(); toast.success("Approval revoked"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const approveAll = async () => { await api.post(`/blocks/${block.id}/approve-all`); load(); toast.success("Sheet approved"); };
  const deleteRow = async (rowId) => {
    const ok = await confirm({ title: "Delete row?", confirmText: "Delete", tone: "destructive" });
    if (!ok) return;
    await api.delete(`/rows/${rowId}`); load();
  };

  // Column drag-reorder
  const onColDragStart = (e, cid) => {
    if (!isTeam) return;
    setDragColId(cid);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", cid);
  };
  const onColDragOver = (e) => { if (dragColId) e.preventDefault(); };
  const onColDrop = (e, overCid) => {
    e.preventDefault();
    if (!dragColId || dragColId === overCid) { setDragColId(null); return; }
    const fromIdx = columns.findIndex((c) => c.id === dragColId);
    const toIdx = columns.findIndex((c) => c.id === overCid);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...columns];
    const [moving] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moving);
    onUpdateBlock(block.id, { config: { ...block.config, columns: next } });
    setDragColId(null);
  };

  const exportCsv = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/blocks/${block.id}/csv`;
    const token = localStorage.getItem("tt_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `sheet_${block.id}.csv`;
        a.click();
      });
  };

  const filtered = rows.filter((r) => {
    if (!search) return true;
    return columns.some((c) => String(r.data?.[c.id] ?? "").toLowerCase().includes(search.toLowerCase()));
  });
  const sorted = sortCol ? [...filtered].sort((a, b) => {
    const av = a.data?.[sortCol] ?? ""; const bv = b.data?.[sortCol] ?? "";
    return sortDir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  }) : filtered;

  const onSort = (cid) => {
    if (sortCol === cid) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortCol(cid); setSortDir("asc"); }
  };

  // Column resize
  const [resizingCol, setResizingCol] = useState(null);
  const startResize = (e, colId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isTeam) return;
    const col = columns.find((c) => c.id === colId);
    const startX = e.clientX;
    const startW = col.width || 180;
    setResizingCol(colId);
    const onMove = (ev) => {
      const w = Math.max(80, startW + (ev.clientX - startX));
      // Live-update locally for smooth feel
      const tw = document.querySelector(`th[data-col-id="${colId}"]`);
      if (tw) tw.style.width = w + "px";
      const cells = document.querySelectorAll(`td[data-col-id="${colId}"]`);
      cells.forEach((c) => { c.style.width = w + "px"; });
    };
    const onUp = (ev) => {
      const w = Math.max(80, startW + (ev.clientX - startX));
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setResizingCol(null);
      // Persist
      const next = columns.map((c) => c.id === colId ? { ...c, width: w } : c);
      onUpdateBlock(block.id, { config: { ...block.config, columns: next } });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const title = block.config?.title || "Sheet";
  const [titleEdit, setTitleEdit] = useState(false);
  const [titleDraft, setTitleDraft] = useState(title);
  useEffect(() => { setTitleDraft(title); }, [title]);
  const saveTitle = async () => {
    if (titleDraft.trim() && titleDraft !== title) {
      await onUpdateBlock(block.id, { config: { ...block.config, title: titleDraft.trim() } });
    }
    setTitleEdit(false);
  };

  return (
    <div data-testid={`sheet-block-${block.id}`}>
      <div className="md:hidden mb-2 flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-zinc-800 bg-zinc-900/50 text-[11px] text-zinc-500">
        <span className="font-mono">!</span> Sheet is best edited on desktop.
      </div>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        {titleEdit && isTeam ? (
          <Input value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={saveTitle} onKeyDown={(e) => { if (e.key === "Enter") saveTitle(); if (e.key === "Escape") { setTitleDraft(title); setTitleEdit(false); } }}
            autoFocus
            className="font-display text-[16px] font-semibold tracking-tight bg-transparent border-zinc-700 h-9 max-w-md" data-testid={`sheet-title-input-${block.id}`} />
        ) : (
          <h3 onDoubleClick={() => isTeam && setTitleEdit(true)}
            className={`font-display text-[16px] font-semibold tracking-tight ${isTeam ? "cursor-text hover:text-white" : ""}`}
            title={isTeam ? "Double-click to rename" : ""}
            data-testid={`sheet-title-${block.id}`}>
            {title}
            {isTeam && <span className="ml-2 text-[10px] text-zinc-700 font-mono uppercase tracking-wider">↳ dbl-click to rename</span>}
          </h3>
        )}
        <div className="flex items-center gap-1.5">
          <div className="relative">
            <Search className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
              className="bg-[#0a0a0a] border-zinc-800 h-8 pl-7 text-[12px] w-44" data-testid={`sheet-search-${block.id}`} />
          </div>
          <Button size="sm" variant="ghost" onClick={exportCsv} className="text-zinc-500 hover:text-white h-8 w-8 p-0" title="Export CSV" data-testid={`sheet-csv-${block.id}`}><Download className="w-3.5 h-3.5" /></Button>
          {isTeam && (
            <Button size="sm" variant="ghost" onClick={() => setEditor((e) => !e)} className="text-zinc-300 hover:text-white h-8 text-[12px]" data-testid={`sheet-config-${block.id}`}>
              <Settings2 className="w-3.5 h-3.5 mr-1" /> Configure
            </Button>
          )}
        </div>
      </div>

      {editor && isTeam && <ColumnEditor block={block} onChange={updateConfig} onClose={() => setEditor(false)} />}

      <div className="border border-zinc-800 rounded-lg overflow-hidden bg-[#0F0F0F] mt-2">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-zinc-950 sticky top-0">
              <tr>
                {columns.map((c) => (
                  <th key={c.id} data-col-id={c.id}
                    style={{ width: c.width ? `${c.width}px` : undefined, minWidth: 80 }}
                    draggable={isTeam}
                    onDragStart={(e) => onColDragStart(e, c.id)}
                    onDragOver={onColDragOver}
                    onDrop={(e) => onColDrop(e, c.id)}
                    onClick={() => onSort(c.id)}
                    className={`relative text-left px-3.5 py-2.5 text-[10px] uppercase tracking-wider text-zinc-500 cursor-pointer select-none hover:text-zinc-200 border-b border-zinc-800 ${dragColId === c.id ? "opacity-40" : ""}`}>
                    <span className="inline-flex items-center gap-1.5">
                      {isTeam && <GripVertical className="w-2.5 h-2.5 text-zinc-700 cursor-grab" />}
                      {c.name} {sortCol === c.id ? (sortDir === "asc" ? "↑" : "↓") : ""}
                    </span>
                    {isTeam && (
                      <span onMouseDown={(e) => startResize(e, c.id)}
                        onClick={(e) => e.stopPropagation()}
                        className={`col-resize-handle ${resizingCol === c.id ? "resizing" : ""}`}
                        title="Drag to resize" />
                    )}
                  </th>
                ))}
                <th className="px-3.5 py-2.5 border-b border-zinc-800"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id} className="border-b border-zinc-900 sheet-row" data-testid={`sheet-row-${r.id}`}>
                  {columns.map((c) => {
                    const writable = isTeam || c.client_writable;
                    // For text-like cells, cap natural column width so long content wraps
                    // instead of pushing the column open. User can drag the resize handle to widen.
                    const isTextish = !c.type || c.type === "text" || c.type === "feedback";
                    const maxW = c.width ? undefined : (isTextish ? 320 : undefined);
                    return (
                      <td key={c.id} data-col-id={c.id}
                        style={{ width: c.width ? `${c.width}px` : undefined, minWidth: 80, maxWidth: maxW }}
                        className="sheet-cell border-r border-zinc-900 last:border-r-0">
                        <CellEditor col={c} value={r.data?.[c.id]} readonly={!writable}
                          row={r} onApproveRow={approveRow} onUnapproveRow={unapproveRow}
                          disabled={editor} allCols={columns} isTeam={isTeam}
                          onCommit={(v) => updateCell(r.id, c.id, v)} />
                      </td>
                    );
                  })}
                  <td className="px-2 py-2.5 align-middle whitespace-nowrap">
                    {isTeam && <Button size="sm" variant="ghost" onClick={() => deleteRow(r.id)} data-testid={`delete-row-${r.id}`} className="text-zinc-600 hover:text-[#EF4444] h-7 w-7 p-0"><Trash2 className="w-3 h-3" /></Button>}
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr><td colSpan={columns.length + 1} className="px-3.5 py-6 text-center text-zinc-600 text-[12px]">No rows yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {(isTeam || block.config?.client_can_add_rows) && (
          <button onClick={addRow} data-testid={`add-row-${block.id}`}
            className="w-full px-3.5 py-2 text-[12px] text-zinc-500 hover:text-white hover:bg-zinc-900/40 transition-colors flex items-center gap-2 border-t border-zinc-900">
            <Plus className="w-3.5 h-3.5" /> Add row
          </button>
        )}
        {isTeam && hasMetaCols && (
          <button onClick={refreshMetaKpis} data-testid={`refresh-meta-${block.id}`}
            className="w-full px-3.5 py-2 text-[12px] text-zinc-500 hover:text-white hover:bg-zinc-900/40 transition-colors flex items-center gap-2 border-t border-zinc-900">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh Meta Ads values from Graph API
          </button>
        )}
      </div>
    </div>
  );
}
